import { DOCUMENT, isPlatformBrowser } from "@angular/common";
import {
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  HostListener,
  Inject,
  OnInit,
  PLATFORM_ID,
  ViewEncapsulation
} from "@angular/core";
import { Meta, MetaDefinition, Title } from "@angular/platform-browser";
import { NavigationEnd, NavigationStart, Router } from "@angular/router";
import { Actions, ofType } from "@ngrx/effects";
import { Store } from "@ngrx/store";
import { filter, Observable, tap } from "rxjs";
import { AffiliateCidService } from "../../services/affiliate-cid/affiliate-cid.service";
import { AnalyticsService } from "../../services/analytics/analytics.service";
import { ContentApiService } from "../../services/api/content-api.service";
import { ComponentLoaderService } from "../../services/component-loader/component-loader.service";
import { CrossDomainTrackingService } from "../../services/cross-domain-tracking/cross-domain-tracking.service";
import { HtmlTagService } from "../../services/html-tag/html-tag.service";
import {
  resetInPageNavigation,
  setWindowLoaded
} from "../../store/global/global.actions";
import { GlobalState } from "../../store/global/global.reducer";
import {
  selectBaseHref,
  selectPageTitle
} from "../../store/global/global.selectors";
import { loadResultsSuccess } from "../../store/search/search.actions";
import { DynamicComponentItem } from "../utils/dynamic-loader/dynamic-component-item";
import { LoaderComponent } from "../utils/dynamic-loader/loader.component";

@Component({
  selector: "dotcom-frontend-page",
  templateUrl: "./page.component.html",
  encapsulation: ViewEncapsulation.None,
  standalone: true,
  imports: [LoaderComponent]
})
export class PageComponent implements OnInit {
  pageData: any | undefined;
  header: any | undefined;
  footer: any | undefined;
  navStart: Observable<NavigationStart>;
  navEnd: Observable<NavigationEnd>;
  isBrowser = isPlatformBrowser(this.platformId);
  baseHref = "";
  pageTitle$: Observable<string> = this.store.select(selectPageTitle);
  adobeScriptLoaded = false;
  pageTitle = "";
  baseHref$: Observable<string> = this.store.select(selectBaseHref);
  headerComponent: DynamicComponentItem | undefined;
  footerComponent: DynamicComponentItem | undefined;
  pageComponent: any | undefined;
  footerCode: any | undefined;
  heroTitle: string | undefined;
  pageName: string | undefined;
  styleId = "at-body-style";
  excludeFromPageView = false;
  preFooterContent: any;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(PLATFORM_ID) private platformId: any,
    private contentApiService: ContentApiService,
    private affiliateCidService: AffiliateCidService,
    private crossDomainTrackingService: CrossDomainTrackingService,
    private htmlTagService: HtmlTagService,
    private analyticsService: AnalyticsService,
    private componentLoaderService: ComponentLoaderService,
    public router: Router,
    private title: Title,
    private meta: Meta,
    private store: Store<{ global: GlobalState }>,
    private actions$: Actions,
    private cdr: ChangeDetectorRef,
    private applicationRef: ApplicationRef
  ) {
    if (this.isBrowser) {
      this.analyticsService.initAdobeLaunchDataLayer();
    }
    this.navStart = router.events.pipe(
      filter((evt) => evt instanceof NavigationStart)
    ) as Observable<NavigationStart>;

    this.navEnd = router.events.pipe(
      filter((evt) => evt instanceof NavigationEnd)
    ) as Observable<NavigationEnd>;

    this.baseHref$.subscribe((baseHref) => {
      this.baseHref = baseHref;
    });

    this.pageTitle$.subscribe((title) => {
      this.pageTitle = title;
    });

    this.actions$.pipe(ofType(loadResultsSuccess)).subscribe((data: any) => {
      const reg = /(?<=“)(.*?)(?=”)/;
      const pageTitle = this.pageTitle.replace(reg, data.searchTerm);
      if (this.isBrowser) {
        const adobeData: any = {
          event: "pageView",
          pageViews: {
            value: 1
          },
          page: {
            appName: "DotCom",
            businessArea: this.pageData.businessUnit,
            pageID: this.pageData.contentLink.id,
            pageTitle: pageTitle,
            pageType: this.pageData.contentType[1],
            testingActivated: this.pageData.activateAdobeTarget,
            url: window.location.href,
            searchKeyWord: data.searchTerm,
            searchCollection: data.collection,
            searchResultsPage: Number(data.page),
            searchTotalResults: data.search?.body
              ? data.search.body.totalMatching
              : 0
          }
        };
        this.analyticsService.pushToDataLayer(adobeData);
      }
    });
  }

  ngOnInit() {
    this.getPageData(this.router.url);
    this.navigationEvents();
    this.affiliateCidService.saveCid();
    this.saveReferrer();
  }

  hidePage() {
    this.htmlTagService.addTag({
      tag: "style",
      identifier: this.styleId,
      id: this.styleId,
      text: "body {opacity: 0 !important}"
    });

    if (this.isBrowser) {
      setTimeout(() => {
        this.htmlTagService.addTag({
          identifier: this.styleId,
          mode: "remove"
        });
      }, 2500);
    }
  }

  navigationEvents() {
    this.navStart.subscribe((e) => {
      const isStableObs = this.applicationRef.isStable
        .pipe(
          tap((isStable) => {
            if (isStable) {
              this.processSsKeysDomains();
              this.processTealiumDomains();
              this.processCubedDomains();
              this.processMidDomains();
              isStableObs.unsubscribe();
            }
          })
        )
        .subscribe();
      this.pageData = undefined;
      this.pageComponent = undefined;
      this.getPageData(e.url);
    });

    this.navEnd.subscribe(() => {
      this.store.dispatch(resetInPageNavigation());
    });
  }

  getPageData(url: string) {
    if (url.startsWith("/preview")) {
      this.contentApiService.getPreviewData(url).subscribe(async (response) => {
        await this.setPageData(response);
      });
    } else {
      // TODO: LGCOM-8122 this should be done using the store similar to what we do for search results
      this.contentApiService.getData(url).subscribe(async (response) => {
        await this.setPageData(response);
      });
    }
  }

  async setPageData(response: any) {
    this.cdr.detectChanges();

    if (response === 404) {
      this.contentApiService.getData("/404/").subscribe(async (response) => {
        await this.processPageData(response);
      });
    } else {
      await this.processPageData(response);
    }
  }

  async processPageData(response: any) {
    this.pageComponent = response.layout;
    this.pageData = response.layout?.data;

    if (this.pageData) {
      if (JSON.stringify(this.header) !== JSON.stringify(response.header)) {
        this.header = undefined;
        this.headerComponent = undefined;
        this.cdr.detectChanges();
        this.header = response.header;

        if (this.header) {
          this.headerComponent =
            await this.componentLoaderService.loadComponent(
              "Header",
              "globals",
              {
                headerData: this.header,
                ctaButtons: this.pageData.ctaButtons,
                isSticky: this.pageData.headerStyle === "sticky-campaign",
                headerStyle: this.pageData.headerStyle,
                secondaryPartnerLogo: this.pageData.secondaryPartnerLogo,
                secondaryPartnerLogoAltText:
                  this.pageData.secondaryPartnerLogoAltText
              }
            );
        }
      }

      if (JSON.stringify(this.footer) !== JSON.stringify(response.footer)) {
        this.footer = undefined;
        this.footerComponent = undefined;
        this.footerCode = undefined;
        this.cdr.detectChanges();
        this.footer = response.footer;

        if (this.footer) {
          this.footerComponent =
            await this.componentLoaderService.loadComponent(
              "Footer",
              "globals",
              {
                footerData: this.footer,
                logoUrl: this.header && this.header["headerLogoUrl"],
                logoImage: this.header && this.header["headerLogoImage"],
                logoAltText: this.header && this.header["headerLogoAltText"],
                oneTrustScript: this.pageData.oneTrustScript
              }
            );
        }
      }

      if (this.pageData.activateAdobeTarget) {
        this.hidePage();
      }

      if (this.footer?.preFooterContent) {
        this.preFooterContent =
          await this.componentLoaderService.contentAreaLoader(
            this.footer.preFooterContent
          );
      }

      if (this.footer?.footerCode) {
        this.footerCode = await this.componentLoaderService.contentAreaLoader(
          this.footer.footerCode
        );
      }

      this.handleSEOData();
      this.addAdobeScript();
      this.addACustomScript();

      if (this.adobeScriptLoaded) {
        this.adobeScriptLoadEvent();
      }
    }
  }

  handleSEOData() {
    this.removeMetaTags("name=author");
    this.removeMetaTags("property='idio:tag'");
    this.removeMetaTags("property='idio:community'");

    this.title.setTitle(this.pageData.browserTitle);

    this.manageMetaTag("title", this.pageData.browserTitle);
    this.manageMetaTag(
      "og:title",
      this.pageData.teaserTitle ||
        this.pageData.pagetitle ||
        this.pageData.browserTitle,
      "property"
    );

    if (this.pageData.authors) {
      const metaAuthors: MetaDefinition[] = [];
      this.pageData.authors.forEach((author: any) => {
        metaAuthors.push({
          name: "author",
          content: `${author.contentLink?.expanded.authorFirstName} ${author.contentLink?.expanded.authorSurname}`
        });
      });

      this.meta.addTags(metaAuthors);
    } else if (this.pageData.seoAuthor || this.pageData.businessUnit) {
      this.manageMetaTag(
        "author",
        this.pageData.seoAuthor || this.pageData.businessUnit
      );
    } else {
      this.manageMetaTag("author", "Legal & General");
    }

    this.manageMetaTag("keywords", this.pageData.seoKeywords);
    this.manageMetaTag("description", this.pageData.seoDescription);
    this.manageMetaTag(
      "og:description",
      this.pageData.seoDescription,
      "property"
    );
    this.manageMetaTag(
      "og:image",
      this.pageData.teaserImage?.url || this.pageData.mainImage?.url,
      "property"
    );
    this.manageMetaTag("date", this.pageData.changed);
    this.manageMetaTag(
      "article:published_time",
      this.pageData.articleDate ||
        this.pageData.indexDate ||
        this.pageData.changed,
      "property"
    );

    if (this.pageData.category) {
      const metaCategories: MetaDefinition[] = [];
      this.pageData.category.forEach((category: any) => {
        metaCategories.push({
          property: "idio:tag",
          content: category.name
        });
      });

      this.meta.addTags(metaCategories);
    }

    if (this.pageData.communityGroupMappings) {
      const metaCommunities: MetaDefinition[] = [];
      this.pageData.communityGroupMappings.forEach((community: any) => {
        metaCommunities.push({
          property: "idio:community",
          content: community.group + " - " + community.name
        });
      });

      this.meta.addTags(metaCommunities);
    }

    this.addCanonicalLink(this.pageData.canonicalUrl);

    if (this.pageData.noIndex && this.pageData.noFollow) {
      this.manageMetaTag("robots", "noIndex, noFollow");
      this.manageMetaTag("googlebot", "noIndex, noFollow");
    } else if (this.pageData.noIndex && !this.pageData.noFollow) {
      this.manageMetaTag("robots", "noIndex");
      this.manageMetaTag("googlebot", "noIndex");
    } else if (this.pageData.noFollow && !this.pageData.noIndex) {
      this.manageMetaTag("robots", "noFollow");
      this.manageMetaTag("googlebot", "noFollow");
    }

    if (!this.router.parseUrl(this.router.url).root?.children["primary"]) {
      this.manageMetaTag("sskeyhosts", this.pageData.ssKeyHosts);
      this.manageMetaTag("midhosts", this.pageData.midHosts);
      this.manageMetaTag("tealiumhosts", this.pageData.tealiumHosts);
      this.manageMetaTag("cubedhosts", this.pageData.cubedHosts);
    } else {
      this.manageMetaTag("sskeyhosts", "");
      this.manageMetaTag("midhosts", "");
      this.manageMetaTag("tealiumhosts", "");
      this.manageMetaTag("cubedhosts", "");
    }

    this.addSchemaJson();
  }

  removeMetaTags(name: string) {
    const tags = this.meta.getTags(name);
    if (tags) {
      tags.forEach((tag) => {
        this.meta.removeTagElement(tag);
      });
    }
  }

  manageMetaTag(name: string, content: string, property = "name") {
    if (content) {
      this.meta.updateTag({ [`${property}`]: name, content });
    } else {
      this.meta.removeTag(`${property}="${name}"`);
    }
  }

  addSchemaJson() {
    const schemaJson = {
      "@context": "https://schema.org",
      "@type": "WebSite",
      url: this.pageData.contentLink.url,
      logo: this.header && this.header.headerLogoImage,
      name: this.pageData.schemaBrand,
      brand: this.pageData.schemaBrand,
      email: this.pageData.schemaEmail,
      address: this.pageData.schemaAddress
    };

    this.htmlTagService.addTag({
      tag: "script",
      identifier: "js-schema",
      text: JSON.stringify(schemaJson),
      type: "application/ld+json",
      mode: this.router.parseUrl(this.router.url).root?.children["primary"]
        ? "remove"
        : "add"
    });
  }

  addCanonicalLink(href: string) {
    this.htmlTagService.addTag({
      tag: "link",
      identifier: "js-canonical",
      rel: "canonical",
      href: href ? href : this.pageData.contentLink.url
    });
  }

  addAdobeScript() {
    if (this.isBrowser && this.pageData.adobeLaunchUrl) {
      const adobeScript = this.htmlTagService.addTag({
        tag: "script",
        identifier: "js-adobe-script",
        type: "text/javascript",
        src: this.pageData.adobeLaunchUrl,
        async: "true"
      });

      adobeScript.addEventListener("load", () => {
        if (!this.adobeScriptLoaded) {
          this.adobeScriptLoadEvent();
        }
        this.adobeScriptLoaded = true;
      });
    }
  }

  addACustomScript() {
    if (this.pageData.customScript) {
      this.htmlTagService.addTag({
        tag: "script",
        identifier: "js-custom-script",
        type: "text/javascript",
        text: this.pageData.customScript
      });
    }
  }

  adobeScriptLoadEvent() {
    if (this.pageData["heroContentArea"][0]) {
      this.heroTitle = this.pageData["heroContentArea"][0]["data"]["heroTitle"];
    }
    if (this.pageData["name"]) {
      this.pageName = this.pageData["name"];
    }

    if (
      this.pageData.contentType["1"] === "SearchResultsPage" ||
      this.pageData.contentType["1"] === "DocumentLibraryPage" ||
      this.pageData.contentType["1"] === "ArticlePage"
    ) {
      this.excludeFromPageView = true;
    }

    if (this.isBrowser && !this.excludeFromPageView) {
      const data: any = {
        event: "pageView",
        pageViews: {
          value: 1
        },
        page: {
          appName: "DotCom",
          businessArea: this.pageData.businessUnit,
          pageID: this.pageData.contentLink.id,
          pageTitle: this.heroTitle || this.pageTitle || this.pageName,
          pageType: this.pageData.contentType[1],
          testingActivated: this.pageData.activateAdobeTarget,
          url: window.location.href
        },
        customer: [
          {
            ids: {
              tealium: this.crossDomainTrackingService.getTealiumKey()
            }
          }
        ]
      };

      this.analyticsService.pushToDataLayer(data);
    }
    // use timeout 0 to wait for all blocks to be loaded
    setTimeout(() => {
      this.analyticsService.scriptLoaded();
    }, 0);
  }

  processSsKeysDomains() {
    if (this.pageData?.ssKeyHosts) {
      this.crossDomainTrackingService.saveSSKey(this.pageData.ssKeyHosts);
    }
  }

  processTealiumDomains() {
    if (this.pageData?.tealiumHosts) {
      this.crossDomainTrackingService.saveTealiumKey(
        this.pageData.tealiumHosts
      );
    }
  }

  processCubedDomains() {
    if (this.pageData?.cubedHosts) {
      this.crossDomainTrackingService.saveCubedKey(this.pageData.cubedHosts);
    }
  }

  processMidDomains() {
    if (this.pageData?.midHosts) {
      this.crossDomainTrackingService.saveMidKey(this.pageData.midHosts);
    }
  }

  saveReferrer() {
    this.crossDomainTrackingService.setReferrerDomain();
  }

  reloadOneTrustPreferences() {
    if ((window as any).OneTrust) {
      (window as any).OneTrust.OnConsentChanged(() => {
        (window as any).location.reload();
      });
    }
  }

  @HostListener("window:load", ["$event"])
  onLoad() {
    this.processSsKeysDomains();
    this.processTealiumDomains();
    this.processCubedDomains();
    this.processMidDomains();
    this.store.dispatch(setWindowLoaded({ windowLoaded: true }));
    this.reloadOneTrustPreferences();
  }
}
